import {Button, Stack, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import type {ColumnDef} from "@tanstack/react-table";
import type {AggregationSurveySubmissionListItem} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {ServerTable, ServerTableFrame, ServerTableProvider, useServerTable} from "../../common/serverTable";
import {DateElement} from "../../common/uiElements/DateElement";
import {InsightsCard} from "../../insights/kit/InsightsCard";
import {SURVEY_EVALUATION_SUBMISSIONS_TABLE_ID} from "../shared/surveyQueryRefresh";
import {buildSurveyEvaluationRequest, SurveyEvaluationScope} from "../shared/useSurveyEvaluation";
import {SurveyListSubmissionDialog} from "../submissions/SurveyListSubmissionDialog";
import {SurveySubmissionGuestCell} from "../submissions/SurveySubmissionCells";

interface Props {
    scope: SurveyEvaluationScope;
    query: string;
    currentCaption: string;
    compareCaption: string;
    onClear: () => void;
}

/** Search results have their own paging and source selection; the evaluation's charts keep their full cohort. */
export default function SurveyEvaluationSubmissionSearch({scope, query, currentCaption, compareCaption, onClear}: Props) {
    const {t} = useTranslation();
    const [period, setPeriod] = useState<'current' | 'compare'>('current');
    // Keep the selected row even when a search is cleared or its results refresh.
    const [selected, setSelected] = useState<AggregationSurveySubmissionListItem | null>(null);
    const compare = period === 'compare' && !!scope.filters.compareRange;
    const evaluation = buildSurveyEvaluationRequest(scope.surveyId!, {
        ...scope.filters,
        range: compare ? scope.filters.compareRange! : scope.filters.range,
    });
    const search = query.trim();
    const columns = useMemo<ColumnDef<AggregationSurveySubmissionListItem>[]>(() => [
        {
            id: 'createdAt', header: t('Submitted'), size: 160,
            cell: ({row}) => <DateElement date={row.original.createdAt}/>,
            meta: {defaultActive: true},
        },
        {
            id: 'reply', header: t('Guest'), size: 400,
            cell: ({row}) => <SurveySubmissionGuestCell submission={row.original}/>,
            meta: {defaultActive: true, grow: true},
        },
    ], [t]);
    const controller = useServerTable({
        tableId: SURVEY_EVALUATION_SUBMISSIONS_TABLE_ID,
        presentation: 'embedded', defaultPageSize: 5,
        columns, getRowId: row => row.id,
        enabled: !!scope.surveyId && !!search,
        queryDependencies: [evaluation, search],
        queryFn: ({pagination}) => SurveyAggregationService.listEvaluationSubmissions({
            evaluation, query: search,
            limit: {offset: pagination.pageIndex * pagination.pageSize, limit: pagination.pageSize,
                sort: [{field: 'createdAt', direction: 'DESC'}]},
        }),
        onRowClick: setSelected,
    });

    return <>
        {!!search && <InsightsCard
            title={t('Matching submissions')}
            actions={<Button size="small" onClick={onClear}>{t('Clear search')}</Button>}
        >
            <Stack gap={1.5}>
                <Typography variant="body2">“{search}” · {compare ? compareCaption : currentCaption}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('Charts still show all submissions in the selected period and filters.')}
                </Typography>
                {!!scope.filters.compareRange && <ToggleButtonGroup
                    exclusive size="small" value={compare ? 'compare' : 'current'}
                    onChange={(_, value) => value && setPeriod(value)}
                    sx={{alignSelf: 'flex-start'}}
                >
                    <ToggleButton value="current">{t('This period')}</ToggleButton>
                    <ToggleButton value="compare">{t('Compare period')}</ToggleButton>
                </ToggleButtonGroup>}
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame><ServerTable/></ServerTableFrame>
                </ServerTableProvider>
            </Stack>
        </InsightsCard>}
        {!!selected && <SurveyListSubmissionDialog submission={selected} onClose={() => setSelected(null)}/>}
    </>;
}
